import React from "react";
import GaugeComponent from "react-gauge-component";
import Footer from "./Footer"; // Import Footer component
const introLogo = "/reportImages/intro-logo.png";

const GaugeWrapper = ({ title, value, min, max }) => {
  const ticks = [];
  for (let i = min; i <= max; i += Math.ceil((max - min) / 30)) {
    // 30 is the number of ticks
    ticks.push({ value: i, label: "" });
  }
  return (
    <div style={{ width: 400, textAlign: "center", position: "relative" }}>
      <GaugeComponent
        value={value}
        minValue={min}
        maxValue={max}
        type="semicircle"
        arc={{
          width: 0.2,
          padding: 0.005,
          style: { border: "1px solid black" },
          cornerRadius: 1,
          subArcs: [
            { limit: value, color: "#3498db" },
            { limit: max, color: "#d6eaf8" },
          ],
        }}
        pointer={{
          value,
          color: "#000",
          length: 0.65,
          width: 10,
          baseColor: "#000",
        }}
        labels={{
          valueLabel: {
            hide: true,
          },
          tickLabels: {
            type: "inner",
            ticks: ticks,
            defaultTickValueConfig: {
              formatTextValue: () => "", // This will return empty string for all tick values
            },
          },
        }}
      />

      <div
        style={{
          position: "absolute",
          left: 50,
          top: 155, // Y center of the gauge SVG
          width: "72%",
          height: 0,
          borderTop: "1px solid #333",
          zIndex: 2,
        }}
      ></div>

      {/* Current Value Label at Angle */}
      {(() => {
        const radius = 100; // distance from center (adjust to move label closer/farther)
        const angleDeg = ((value - min) / (max - min)) * 180;
        const angleRad = (Math.PI * (180 - angleDeg)) / 180; // Invert to match semi-circle left-to-right
        const cx = 200; // Center X (half of SVG width)
        const cy = 120; // Center Y (bottom of semi-circle)
        const x = cx + radius * Math.cos(angleRad);
        const y = cy - radius * Math.sin(angleRad);

        return (
          <div
            style={{
              position: "absolute",
              left: `${x - 50}px`,
              top: `${y - 10}px`,
              transform: "translate(-50%, -50%)",
              textAlign: "center",
            }}
          >
            <div style={{ fontWeight: "bold", fontSize: "12px" }}>
              {value.toLocaleString()}
              <div
                style={{
                  fontSize: "9px",
                  lineHeight: 0.3,
                  fontWeight: "normal",
                }}
              >
                Current
              </div>
            </div>
          </div>
        );
      })()}

      {/* Min Label */}
      <div
        style={{
          position: "absolute",
          bottom: "10px",
          left: 30,
          textAlign: "left",
        }}
      >
        <div style={{ fontWeight: "bold", fontSize: 12 }}>{min}</div>
        <div style={{ fontSize: "9px", lineHeight: 0.3 }}>Dead Level</div>
      </div>

      {/* Max Label */}
      <div
        style={{
          position: "absolute",
          bottom: "10px",
          right: 30,
          textAlign: "right",
        }}
      >
        <div style={{ fontWeight: "bold", fontSize: 12 }}>{max}</div>
        <div style={{ fontSize: "9px", lineHeight: 0.2 }}>Max Capacity</div>
      </div>

      {/* Dam Title */}
      <div style={{ fontWeight: "bold", fontSize: 14, marginTop: -10 }}>
        {title}
      </div>
    </div>
  );
};

const display = (val, isPercent = false) => {
  if (val === null || val === undefined) return "-";
  const num = Number(val);
  if (isNaN(num)) return "-";
  const isNegative = num < 0 || Object.is(num, -0);
  return (
    <span style={{ color: isNegative ? "#DC2626" : undefined }}>
      {isNegative ? "-" : ""}
      {Math.abs(num).toLocaleString(undefined, {
        minimumFractionDigits: isPercent ? 2 : 0,
        maximumFractionDigits: isPercent ? 2 : 2,
      })}
      {isPercent ? "%" : ""}
    </span>
  );
};

const highlightChangeWord = (text) => {
  return text?.split(" ").map((word, index) => {
    const clean = word.replace(/[^a-zA-Z]/g, ""); // Remove punctuation

    if (
      clean.toLowerCase() === "increased" ||
      clean.toLowerCase() === "increase"
    ) {
      return (
        <span key={index} className="increased">
          {word + " "}
        </span>
      );
    }

    if (
      clean.toLowerCase() === "decreased" ||
      clean.toLowerCase() === "decrease"
    ) {
      return (
        <span key={index} className="decreased">
          {word + " "}
        </span>
      );
    }

    return word + " ";
  });
};

const getDamSummaryText = (damData, damName) => {
  if (!damData) return "";

  const inflowChange = damData.change?.inflow ?? 0;
  const outflowChange = damData.change?.outflow ?? 0;

  const inflowDirection =
    inflowChange > 0
      ? "increased"
      : inflowChange < 0
      ? "decreased"
      : "remained unchanged";
  const outflowDirection =
    outflowChange > 0
      ? "increased"
      : outflowChange < 0
      ? "decreased"
      : "remained unchanged";

  if (
    (inflowDirection === "increased" && outflowDirection === "increased") ||
    (inflowDirection === "decreased" && outflowDirection === "decreased")
  ) {
    return `At ${damName} Dam, inflows and outflows ${inflowDirection} during this week.`;
  }

  return `At ${damName} Dam, inflows ${inflowDirection} while outflows ${outflowDirection} during this week.`;
};

const DamsSummary = ({
  data,
  currentWeekLabel,
  previousWeekLabel,
  endDate,
  tarbelaLevels,
  manglaLevels,
}) => {
  const dams = data?.map((d) => {
    const curr = d.currentWeek;
    const prev = d.previousWeek;
    const inflowChange = curr.inflow - prev.inflow;
    const outflowChange = curr.outflow - prev.outflow;
    const inflowPct = prev.inflow
      ? ((inflowChange / prev.inflow) * 100).toFixed(2)
      : "-";
    const outflowPct = prev.outflow
      ? ((outflowChange / prev.outflow) * 100).toFixed(2)
      : "-";
    return {
      name: d.Dams,
      ...curr,
      prevInflow: prev.inflow,
      prevOutflow: prev.outflow,
      inflowChange,
      outflowChange,
      inflowPct,
      outflowPct,
    };
  });

  const getDaySuffix = (day) => {
    // 11 12 and 13 is an exception for 'th'
    if (day % 10 === 1 && day !== 11) return "st";
    if (day % 10 === 2 && day !== 12) return "nd";
    if (day % 10 === 3 && day !== 13) return "rd";
    return "th";
  };

  return (
    <div id="dams-section">
      <img src={introLogo} alt="PAR Logo" className="toc-logo" />
      <div style={{ borderBottom: "2px solid #ccc", margin: "0px 40px" }}></div>
      <div className="dams-section">
        <h3 className="section-title">3d. Dams Summary</h3>
        <dic>
          <h6 className="fw-bold">i. Tarbela</h6>
          <p>
            {highlightChangeWord(
              getDamSummaryText(
                data.find((d) => d.Dams === "Tarbela"),
                "Tarbela"
              )
            )}
          </p>
        </dic>
        <h6 className="fw-bold">ii. Mangla</h6>
        <p>
          {highlightChangeWord(
            getDamSummaryText(
              data.find((d) => d.Dams === "Mangla"),
              "Mangla"
            )
          )}
        </p>
        <h6 className="toc-heading">Dams Week on Week Summary</h6>
        <table className="table table-striped comparison-table">
          <thead>
            <tr>
              <th rowSpan={3} style={{ width: "100px" }}>
                Dams
              </th>
              <th colSpan={2} rowSpan={2} style={{ width: "170px" }}>
                <span>
                  {currentWeekLabel?.split("\n").map((line, idx) => (
                    <div key={idx}>
                      {line}
                      <br />
                    </div>
                  ))}
                </span>
              </th>
              <th colSpan={2} rowSpan={2} style={{ width: "170px" }}>
                <span>
                  {previousWeekLabel?.split("\n").map((line, idx) => (
                    <div key={idx}>
                      {line}
                      <br />
                    </div>
                  ))}
                </span>
              </th>
              <th colSpan={4}>Week on Week</th>
            </tr>
            <tr>
              <th colSpan={2}>Change</th>
              <th colSpan={2}>%</th>
            </tr>
            <tr>
              <th className="inflow-darkblue">Inflow</th>
              <th className="outflow-darkgreen">Outflow</th>
              <th className="inflow-lightblue">Inflow</th>
              <th className="inflow-lightgreen">Outflow</th>
              <th>Inflow</th>
              <th>Outflow</th>
              <th>Inflow</th>
              <th>Outflow</th>
            </tr>
          </thead>
          <tbody>
            {dams?.map((d, idx) => (
              <tr key={idx}>
                <td>{d.name}</td>
                <td>{display(d.inflow)}</td>
                <td>{display(d.outflow)}</td>
                <td>{display(d.prevInflow)}</td>
                <td>{display(d.prevOutflow)}</td>
                <td>{display(d.inflowChange)}</td>
                <td>{display(d.outflowChange)}</td>
                <td>{display(d.inflowPct, true)}</td>
                <td>{display(d.outflowPct, true)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="source-text">Source: Pakistan Agriculture Research</p>
        <h6 className="image-caption mt-3">
          Dam Level (
          {(() => {
            const date = new Date(endDate);
            const day = date.getDate();
            return `${day}${getDaySuffix(day)} ${date.toLocaleDateString(
              "en-GB",
              {
                month: "long",
                year: "numeric",
              }
            )}`;
          })()}
          )
        </h6>
        <div className="gauge-row">
          <GaugeWrapper
            title="Tarbela"
            value={tarbelaLevels ?? 1403}
            min={1402}
            max={1550}
          />
          <GaugeWrapper
            title="Mangla"
            value={manglaLevels ?? 1051}
            min={1050}
            max={1242}
          />
        </div>
        <div style={{ marginTop: "350px" }}>
          <Footer footerNumber="8" footerText="Weekly Water & Weather Report" />
        </div>
      </div>
    </div>
  );
};

export default DamsSummary;
